/**
 * FlowNotes API Client - Conversations
 * AI conversation management functions
 */

import { fetchWithAuth, API_BASE_URL } from './core';

export interface AIConversation {
  id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  is_active: boolean;
  status?: 'active' | 'completed' | 'error';
  metadata: Record<string, any>;
  messages?: AIMessage[];
  workspace_scope?: string[] | null;
}

export interface AIMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  references: Array<{type: string; id?: string; title?: string; path?: string}>;
  created_at: string;
  tokens_used?: number;
  metadata: Record<string, any>;
}

/**
 * Get all conversations
 */
export async function getConversations(options: {
  page?: number;
  limit?: number;
  include_deleted?: boolean;
  include_archived?: boolean;
  archived_only?: boolean;
} = {}): Promise<{
  success: boolean;
  conversations: AIConversation[];
  pagination: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean };
}> {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.include_deleted) params.append('include_deleted', 'true');
  if (options.include_archived !== undefined) {
    params.append('include_archived', options.include_archived.toString());
  }
  if (options.archived_only) {
    params.append('archived_only', 'true');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations?${params}`);
  if (!response.ok) throw new Error('Failed to fetch conversations');
  return response.json();
}

/**
 * Get active conversations (for tabs)
 */
export async function getActiveConversations(): Promise<{
  success: boolean;
  conversations: AIConversation[];
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/active`);
  if (!response.ok) throw new Error('Failed to fetch active conversations');
  return response.json();
}

/**
 * Get single conversation with messages
 */
export async function getConversation(
  id: string,
  includeMessages = true
): Promise<{
  success: boolean;
  conversation: AIConversation;
}> {
  const params = new URLSearchParams();
  if (!includeMessages) params.append('include_messages', 'false');

  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}?${params}`);
  if (!response.ok) {
    const err = new Error('Failed to fetch conversation') as Error & { status: number };
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * Create new conversation
 */
export async function createConversation(data: {
  title?: string;
  is_active?: boolean;
  metadata?: Record<string, any>;
}): Promise<{
  success: boolean;
  conversation: AIConversation;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create conversation');
  return response.json();
}

/**
 * Update conversation
 */
export async function updateConversation(
  id: string,
  updates: {
    title?: string;
    is_active?: boolean;
    metadata?: Record<string, any>;
  }
): Promise<{
  success: boolean;
  conversation: AIConversation;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) throw new Error('Failed to update conversation');
  return response.json();
}

/**
 * Archive conversation
 */
export async function archiveConversation(id: string): Promise<{
  success: boolean;
  conversation: { id: string; title: string; archived_at: string };
  message: string;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/archive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: 'default' }),
  });
  if (!response.ok) throw new Error('Failed to archive conversation');
  return response.json();
}

/**
 * Unarchive conversation
 */
export async function unarchiveConversation(id: string): Promise<{
  success: boolean;
  conversation: { id: string; title: string; archived: boolean };
  message: string;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/unarchive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: 'default' }),
  });
  if (!response.ok) throw new Error('Failed to unarchive conversation');
  return response.json();
}

/**
 * Rename conversation
 */
export async function renameConversation(
  id: string,
  title: string
): Promise<{
  success: boolean;
  conversation: { id: string; title: string };
  message: string;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/rename`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, user_id: 'default' }),
  });
  if (!response.ok) throw new Error('Failed to rename conversation');
  return response.json();
}

/**
 * AI auto-generate conversation title
 */
export async function autoNameConversation(id: string): Promise<{
  success: boolean;
  conversation: { id: string; title: string };
  message: string;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/auto-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: 'default' }),
  });
  if (!response.ok) throw new Error('Failed to auto-name conversation');
  return response.json();
}

/**
 * AI suggest conversation title (preview only - does not persist).
 * Pass prior suggestions in `avoid` to shuffle / try again.
 */
export async function suggestConversationTitle(
  id: string,
  options?: { avoid?: string[] },
): Promise<{
  success: boolean;
  title: string;
  message: string;
  model?: string;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/suggest-title`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: 'default',
      avoid: options?.avoid ?? [],
    }),
  });
  if (!response.ok) throw new Error('Failed to suggest conversation title');
  return response.json();
}

/**
 * Delete conversation (soft delete by default)
 */
export async function deleteConversation(
  id: string,
  hardDelete = false
): Promise<{
  success: boolean;
  message: string;
  id: string;
}> {
  const params = new URLSearchParams();
  if (hardDelete) params.append('hard_delete', 'true');

  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}?${params}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete conversation');
  return response.json();
}

/**
 * Add message to conversation
 */
export async function addMessageToConversation(
  conversationId: string,
  message: {
    role: 'user' | 'assistant' | 'system';
    content: string;
    references?: Array<{type: string; id?: string; title?: string; path?: string}>;
    tokens_used?: number;
    metadata?: Record<string, any>;
  }
): Promise<{
  success: boolean;
  message: AIMessage;
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(message),
  });
  if (!response.ok) throw new Error('Failed to add message');
  return response.json();
}

/**
 * Search conversations
 */
export async function searchConversations(
  query: string,
  searchMessages = true,
  limit = 20
): Promise<{
  success: boolean;
  query: string;
  results: AIConversation[];
  count: number;
}> {
  const params = new URLSearchParams({
    q: query,
    limit: limit.toString(),
    search_messages: searchMessages.toString(),
  });

  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/search?${params}`);
  if (!response.ok) throw new Error('Failed to search conversations');
  return response.json();
}

/**
 * Export conversation as markdown
 */
export async function exportConversation(id: string): Promise<Blob> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/conversations/${id}/export`);
  if (!response.ok) throw new Error('Failed to export conversation');
  return response.blob();
}
